import { Button } from '@shared/components/ui/button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import { faTimes, faChevronDown, faCircleCheck } from '@fortawesome/pro-light-svg-icons';
import { memo, useMemo, useCallback, useState } from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@shared/components/ui/dropdown-menu';
import { usePermissions } from '@shared/hooks/usePermissions';

type BulkActionResult = Promise<boolean> | boolean | void;

interface SelectionToolbarProps {
  selectedCount: number;
  onClose: () => void;
  onConfirmSelected: () => BulkActionResult;
  confirmableCount?: number;
}

export const SelectionToolbar = memo(
  ({ selectedCount, onClose, onConfirmSelected, confirmableCount = 0 }: SelectionToolbarProps) => {
    const { t } = useTranslation('codCourierFiles');
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const { canAccess } = usePermissions();

    const isMobile = typeof window !== 'undefined' ? window.innerWidth < 768 : false;

    // 🎯 Verificar permiso para procesar archivos
    const canProcess = canAccess(['tenant_3pl'], ['cod-payments.files.process']);

    // Memoizar las traducciones
    const translations = useMemo(
      () => ({
        selected: t('selection.selected', 'seleccionados'),
        confirm: t('selection.confirm', 'Confirmar Archivos'),
        actions: t('selection.actions', 'Acciones'),
      }),
      [t],
    );

    // Handler para confirmar archivos - abrir modal directamente sin confirmación previa
    const handleConfirm = useCallback(() => {
      if (confirmableCount > 0 && canProcess) {
        onConfirmSelected();
      }
    }, [onConfirmSelected, confirmableCount, canProcess]);

    const handleClose = useCallback(() => {
      onClose();
    }, [onClose]);

    // Si no hay elementos seleccionados, no renderizar nada
    if (selectedCount === 0) {
      return null;
    }

    return (
      <>
        <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50">
          <div className="bg-card-bg border border-card-border rounded-lg p-2 shadow-lg">
            <div className="flex items-center gap-4">
              {/* Contador de seleccionados */}
              <div className="flex items-center gap-2">
                <span className="w-6 h-6 bg-primary text-white text-sm rounded-full flex items-center justify-center">
                  {selectedCount}
                </span>
                <span className="text-sm font-medium">{translations.selected}</span>
              </div>

              {/* Versión Desktop: Botón de confirmar */}
              {!isMobile && canProcess && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleConfirm}
                  disabled={confirmableCount === 0}
                  className={`border-card-border bg-transparent ${confirmableCount === 0 ? 'opacity-50' : ''}`}
                >
                  <FontAwesomeIcon icon={faCircleCheck} className="w-3 h-3 mr-1 text-success" />
                  {translations.confirm} ({confirmableCount})
                </Button>
              )}

              {/* Versión Mobile: Dropdown de acciones */}
              {isMobile && canProcess && (
                <DropdownMenu open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" className="border-card-border bg-transparent">
                      {translations.actions}
                      <FontAwesomeIcon icon={faChevronDown} className="ml-2 w-3 h-3" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-52 border-card-border">
                    <DropdownMenuItem
                      onClick={handleConfirm}
                      disabled={confirmableCount === 0}
                      className={confirmableCount === 0 ? 'opacity-50' : ''}
                    >
                      <FontAwesomeIcon icon={faCircleCheck} className="mr-2 w-3.5 text-success" />
                      {translations.confirm} ({confirmableCount})
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}

              {/* Botón cerrar */}
              <Button variant="ghost" size="sm" onClick={handleClose} className="p-0 hover:bg-transparent">
                <FontAwesomeIcon icon={faTimes} className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </>
    );
  },
);

SelectionToolbar.displayName = 'SelectionToolbar';
